/**
 * CalendarUI is a calendar interface built upon the Toast UI Calendar.
 *
 * Details regarding what is expected can be found on the CalendarUIConfig class.
 */

// https://ui.toast.com/tui-calendar
// https://github.com/nhn/tui.calendar/blob/main/docs/en/guide/getting-started.md
// https://nhn.github.io/tui.calendar/latest/tutorial-01-monthly-view-basic
// https://github.com/nhn/tui.calendar/blob/b53e765e8d896ab7c63d9b9b9515904119a72f46/apps/calendar/examples/scripts/mock-data.js#L149
// https://www.npmjs.com/package/@toast-ui/calendar#load
//@ts-ignore - this module is not declared correctly, so using @ts-ignore is our only option
import TUICalendar, {type EventObject, type Options} from '@toast-ui/calendar';

export class DateRange {
	constructor(
		public start: Date, 
		public end: Date
	) {
	}

	getMiddle(): Date {
		if (!this.start || !this.end) {
			return null;
		}
		return new Date(this.start.getTime() + (this.end.getTime() - this.start.getTime()) / 2);
	}
}
class ICalendarUIConfig {
	elem: HTMLElement;
	observeElements: HTMLElement[]; // When the subtree of these elements change we reloadEvents()
	calendarViewTrigger?: HTMLElement;

	dateRangeRadio?: HTMLInputElement;
	dateRangeStart?: HTMLInputElement;
	dateRangeEnd?: HTMLInputElement;
}
/**
 * elem: HTMLElement is wher calendar goes
 * observeElements: HTMLElement[] When the subtree of these elements change we reloadEvents()
 * 
 * A default implemenation of createEvent() is included as here. You can override this function to provide your own implementation.
 * - Each event to be marked up with schema.org/Event
 * - Each event can provide links with class="calendarui-popup-link" that will be added to the event's popup
 * - Each event specifies date time ranges with one or more areas with class="calendarui-datetime", and within each schema.org/startDate and schema.org/endDate
 * - Each event can specify a calendarId by including a data-calendarui-calendarId attribute
 * 
 * Default implementation of getDateRange() and setDateRange() also exist. They expect:
 * - Two input fields with id="dateRange__from" and id="dateRange__to" for the date range
 */
export class CalendarUIConfig extends ICalendarUIConfig {
	constructor(
		{
			elem,
			observeElements,
			calendarViewTrigger,

			dateRangeRadio,
			dateRangeStart,
			dateRangeEnd,
		} : ICalendarUIConfig
	) {
		super();
		this.elem = elem;
		this.observeElements = observeElements;
		this.calendarViewTrigger = calendarViewTrigger;

		this.dateRangeRadio = dateRangeRadio ?? document.getElementById('dateRange') as HTMLInputElement;
		this.dateRangeStart = dateRangeStart ?? document.getElementById('dateRange__from') as HTMLInputElement;
		this.dateRangeEnd = dateRangeEnd ?? document.getElementById('dateRange__to') as HTMLInputElement;
	}

	getDateRange(): DateRange {
		return new DateRange(this.dateRangeStart?.valueAsDate, this.dateRangeEnd?.valueAsDate);
	}
	setDateRange(dateRange : DateRange) {
		// Change to date range option
		if (this.dateRangeRadio) {
			this.dateRangeRadio.checked = true;
			this.dateRangeRadio.dispatchEvent(new Event('change', { 'bubbles': true }));
		}

		if (this.dateRangeStart) {
			this.dateRangeStart.valueAsDate = dateRange.start;
			this.dateRangeStart.dispatchEvent(new Event('change', { 'bubbles': true }));
		}
		if (this.dateRangeEnd)
			this.dateRangeEnd.valueAsDate = dateRange.end;
			this.dateRangeStart.dispatchEvent(new Event('change', { 'bubbles': true }));
	}

	getObserveElements(): HTMLElement[] {
		return this.observeElements;
	}

	createEvents(): EventObject[] {
		const events: EventObject[] = [];

		document.querySelectorAll('[itemtype="https://schema.org/Event"]').forEach(eventElem => {
			const title = eventElem.querySelector('[itemprop="name"]')?.textContent ?? 'Untitled Event';
			const url = eventElem.querySelector('[itemprop="url"]')?.getAttribute('href');

			// Location
			let location: string;
			const locationElem = eventElem.querySelector('[itemprop="location"]');
			location = !locationElem ? null : ( locationElem.querySelector('[itemprop="name"]')?.textContent ?? locationElem.textContent );

			// Links - All a.calendarui-popup-link we add as links within the event's popup
			//   NOTE: we stripe all wp-* classes from the anchor and also set target="_blank"
			let linksHTML = '';
			eventElem.querySelectorAll('a.calendarui-popup-link').forEach(link => {
				const newLink = link.cloneNode(true) as HTMLElement;

				newLink.setAttribute('target', '_blank');
				newLink.className = '';
				// newLink.classList.forEach(c => {
				// 	if (c.startsWith('wp-')) {
				// 		newLink.classList.remove(c);
				// 	}
				// });

				linksHTML += newLink.outerHTML;
			});

			// Body
			let body = eventElem.querySelector('[itemprop="description"]')?.textContent ?? '';
			if (linksHTML.length > 0) {
				body += '<div class="toastui-calendar-template-popupDetailBody__links">' + linksHTML + '</div>';
			}

			const eventProps:  EventObject= {
					id: eventElem.getAttribute('id'),
					title: `<a href="${url}">${title}</a>`,
					body,
					location,

					state: eventElem.querySelector('[itemprop="eventStatus"]')?.textContent ?? '',

					raw: { url, },
					attendees: null,
					isReadOnly: true,

					calendarId: eventElem.getAttribute('data-calendarui-calendarId')
					            ?? eventElem.querySelector('[data-calendarui-calendarId]')?.getAttribute('data-calendarui-calendarId'),
			};

			const addDate = (startTime: Date, endTime: Date) => {
				if (!endTime) endTime = startTime;
				if (!startTime) startTime = endTime;

				events.push({
					...eventProps,
					start: startTime,
					end: endTime,
				} as EventObject);
			};
			const createDate = function(value: string): Date {
				let date: Date = value ? new Date(value) : null;
				if (date && isNaN(date.getTime())) {
					date = null;
				}
				return date;
			};

			// In order to group start and end times, we require each to be surrounded by an element marked with 'calendarui-datetime' class
			const dateTimeElems = eventElem.querySelectorAll('.calendarui-datetime');
			if (dateTimeElems.length > 0) {
				// Add all .calendarui-datetime elements
				dateTimeElems.forEach(dateTime => {
					const startTime = createDate(dateTime.querySelector('[itemprop="startDate"]')?.getAttribute('content'));
					const endTime = createDate(dateTime.querySelector('[itemprop="endDate"]')?.getAttribute('content'));
					addDate(startTime, endTime);
				});
			} else {
				// No .calendarui-datetime elements
				const startTime = createDate(eventElem.querySelector('[itemprop="startDate"]')?.getAttribute('content'));
				const endTime = createDate(eventElem.querySelector('[itemprop="endDate"]')?.getAttribute('content'));
				if (startTime || endTime) {
					addDate(startTime, endTime);
				}
			}
		});

		return events;
	}
}

export default class CalendarUI {
	// TODO: colors won't be consistent since assigning of colors based on which event type run into first. We should define colors on taxonomy we use for calendarId, and output their colors as data attributes on filters or some where else.
	private calendarMap: Map<string, string> = new Map();
	private pastelColorsIndex = 0;
	private pastelColors = [ '#dbc7c7', '#b8dbd9', '#d3c1c0', '#f0e0b2', '#dcc7b6', '#fde9e0', '#ebd3d3', '#b2dbdd', '#d1dede', '#eadbd1',
				'#ffc1ca', '#ffe1e4', '#f5e1da', '#e4eab4', '#e4dfd9', '#d9d9e4', '#ebd9e4', '#dcd0e4', '#eadbd9', '#d9d9b4' ];

	private initialized: boolean = false;
	private cal: TUICalendar;

	constructor(private config: CalendarUIConfig) {
		const elem = config.elem;

		if (elem) {
			this.calendarMap.set('default', this.pastelColors[this.pastelColorsIndex++]);

			if (elem.offsetParent !== null) {
				// Calendar element is already visible
				this.init();
			}

			// When the calendar tab is clicked, setup the calendar if not already (also selects Date Range)
			if (config.calendarViewTrigger) {
				config.calendarViewTrigger.addEventListener('click', () => {
					// Init calendar
					this.init();
				});
			}
		}
	};

	getDefaultDate() {
		return new Date();
	}

	/**
	 * Automatically called when the calendar is visible or config.calendarViewTrigger is clicked
	 * @returns {void}
	 */
	init() {
		const elem = this.config.elem;

		if (this.initialized) {
			// Already setup
			this.updateDateRange();
			return;
		}

		// Setup
		this.initialized = true;

		const viewNameSelect = document.querySelector('.navbar__viewname') as HTMLSelectElement;
		const defaultView: Options['defaultView'] = (viewNameSelect.value as Options['defaultView']) ?? 'month';

		let cal = this.cal = new TUICalendar(elem, {
			usageStatistics: false,

			defaultView: defaultView,
			isReadOnly: true,

			useDetailPopup: true,
			useFormPopup: true,

			// https://github.com/nhn/tui.calendar/blob/main/docs/en/apis/template.md
			template: {
				popupDetailTitle({ title }: any) {
					return title.replace(/<a /g, '<a target="_blank" ');
				},
			},

			week: { taskView: false, },
		});

		// Set date to focus calendar on
		const dateRange = this.config.getDateRange();
		let date;
		if (dateRange.start && dateRange.end) {
			// Uses the middle of the range
			date = dateRange.getMiddle();
		} else {
			date = this.getDefaultDate();
		}
		cal.setDate(date);

		// Re-render the calendar when resizing a window (for popup placement)
		window.addEventListener('resize', () => cal.render());

		// Position poopup (since Toast UI Calendar does not position correctly)
		cal.on('clickMoreEventsBtn', () => {
			this.positionPopup('.toastui-calendar-floating-layer:has(.toastui-calendar-event-detail-popup-slot:empty) .toastui-calendar-see-more-container');
		});
		cal.on('clickEvent', () => {
			this.positionPopup('.toastui-calendar-popup-container');
		});

		// Click in cell, open More dialog. We do this primarily because otherwise it is very hard to open it on mobile.
		elem.addEventListener('click', function (e) {
			if (e.target instanceof HTMLElement) {
				if (
					e.target.classList.contains('toastui-calendar-daygrid-cell')
					|| e.target.classList.contains('toastui-calendar-grid-cell-header')
				) {
					// Clicking on a cell 
					const moreButton = e.target.querySelector('.toastui-calendar-grid-cell-more-events');
					if (moreButton) {
						(moreButton as HTMLElement).click();
					}
				}
			}
		});

		// Calendar navigation
		document.querySelector('.navbar__prev').addEventListener('click', () => {
			cal.prev();
			this.updateDateRange(); // this.update();
		});
		document.querySelector('.navbar__next').addEventListener('click', () => {
			cal.next();
			this.updateDateRange(); // this.update();
		});
		document.querySelector('.navbar__today').addEventListener('click', () => {
			cal.today();
			this.updateDateRange(); // this.update();
		});
		viewNameSelect.addEventListener('change', (e) => {
			const targetViewName = (e.target as HTMLSelectElement).value;
			if (targetViewName == 'month' || targetViewName == 'week' || targetViewName == 'day') {
				cal.changeView(targetViewName);
				this.updateDateRange(); // this.update();
			}
		});

		// Initial update
		this.updateDateRange();
		this.reloadEvents();
	}

	private positionPopup(popupSelector: string) {
		const popup = document.querySelector(popupSelector) as HTMLElement;
		if (popup) {
			// A popup (More or event detail) has been opened, reposition
			const elem = this.config.elem;
			const containerL = elem.getBoundingClientRect().left + document.documentElement.scrollLeft;
			const containerT = elem.getBoundingClientRect().top + document.documentElement.scrollTop;
			popup.style.top = (parseFloat(popup.style.top) - containerT) + 'px';
			if (!popup.classList.contains('persistentPopup')) {
				popup.style.left = (parseFloat(popup.style.left) - containerL) + 'px';
			}
			popup.classList.add('persistentPopup'); // for .toastui-calendar-popup-container that opens when clicking on event inside "more" popup
		} else {
			// Not there yet, timeout (yeah, observer did not always execute, this is reliable)
			setTimeout(() => this.positionPopup(popupSelector), 200);
		}
	}

	updateDateRange() {
		const cal = this.cal;

		const start = cal.getDateRangeStart().toDate();
		const end = cal.getDateRangeEnd().toDate();

		// Update 'header' above calendar view to display the current date range being viewed
		const viewType = cal.getViewName();
		const range = document.querySelector('.navbar__range');
		switch (viewType) {
			case 'month':
				const middle = new DateRange(start, end).getMiddle();
				range.textContent = middle.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
				break;
			case 'week':
				let label;
				if (start.getMonth() == end.getMonth()) {
					label = start.toLocaleDateString('en-CA', { year: 'numeric', month: 'long' });
				} else {
					label = start.toLocaleDateString('en-CA', { month: 'short' })
						+ ' – ' + end.toLocaleDateString('en-CA', { month: 'short', year: 'numeric' });
				}
				range.textContent = label;
				break;
			case 'day':
				range.textContent = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
				break;
		}

		// Update date range input fields
		this.config.setDateRange(new DateRange(
			new Date(start.toISOString().slice(0, 10)),
			new Date(end.toISOString().slice(0, 10))
		));
	}

	/**
	 * Reloads the events on the calendar. Events will be loaded by calling config.createEvents(). 
	 */
	reloadEvents() {
		// Gets list of events
		const events = this.config.createEvents();

		// Set background color if one is not set
		for (const event of events) {
			if (!event.backgroundColor) {
 				const calendarId = event.calendarId ?? 'default'
				if (!this.calendarMap.has(calendarId)) {
					this.calendarMap.set(calendarId, this.pastelColors[this.pastelColorsIndex++]);
				}
				event.backgroundColor = this.calendarMap.get(calendarId);
			}
		}

		// Updates calendar with events
		this.cal.clear();
		this.cal.createEvents(events);

		// Observe events, and area that contains the events, if they change we reloadEvens()
		const observeElements = this.config.getObserveElements();
		if (observeElements) {
			const observer = new MutationObserver(() => {
				this.reloadEvents();
			});
			const observeOpts = { childList: true, subtree: true };
			for (const elem of observeElements) {
				if (elem.getAttribute('data-calendarui-observed') != 'true') {
					elem.setAttribute('data-calendarui-observed', 'true');
					observer.observe(elem, observeOpts);
				}
			}
		}
	}
}
